import React, { Suspense, useEffect, useState } from "react";
import {
  Routes,
  BrowserRouter as Router,
  Route,
  useNavigate,
  Navigate,
  Outlet,
} from "react-router-dom";
import { usePasswordless } from "amazon-cognito-passwordless-auth/react";
import { AuthProvider } from "./contexts/auth-context";
import { HistoryProvider } from "./contexts/history-context";

import StepUpAuth from "./pages/settings/stepupauth/StepUpAuth";

const Login = React.lazy(() => import("./pages/authentication/Login"));
const Dashboard = React.lazy(() => import("./pages/dashboard/Dashboard"));
const Upload = React.lazy(() => import("./pages/upload/Upload"));
const Samples = React.lazy(() => import("./pages/samples/Samples"));
const Contact = React.lazy(() => import("./pages/settings/contact/Contact"));
const ViewSample = React.lazy(() => import("./pages/samples/ViewSample"));
const Farms = React.lazy(() => import("./pages/farms/Farms"));
const AddFarm = React.lazy(() => import("./pages/farms/AddFarm"));
const ViewFarm = React.lazy(() => import("./pages/farms/ViewFarm"));
const UkHeatmapDemo = React.lazy(() => import("./pages/heatmap/UkHeatmapDemo"));
const Membership = React.lazy(() => import("./pages/organization/membership/Membership"));
const OrganizationUsers = React.lazy(() => import("./pages/organization/users/Users"));
const OrganizationDashboard = React.lazy(() => import("./pages/organization/dashboard/Dashboard"));
const Organizations = React.lazy(() => import("./pages/platform/organizations/Organizations"));
const Users = React.lazy(() => import("./pages/platform/users/Users"));


const App = () => {
  const {
    signOut,
    signInStatus,
    toggleShowAuthenticatorManager,
    tokensParsed,
    tokens,
  } = usePasswordless();
  const [showStepUpAuth, setShowStepUpAuth] = useState(false);

  useEffect(() => {
    if (showStepUpAuth && signInStatus !== "SIGNED_IN") {
      setShowStepUpAuth(false);
    }
  }, [showStepUpAuth, signInStatus]);

  //basic function to check whether the public routes should be called if the signin status is not signed in
  const PublicRoute = ({ children, signInStatus }: { children: React.ReactNode; signInStatus: string }) => {
    const navigate = useNavigate();

    useEffect(() => {
      if (signInStatus == "SIGNED_IN") {
        navigate("/dashboard");
      }
    }, [signInStatus, navigate]);

    return children;
  };

  // basic function to check whether the protected routes
  const ProtectedRoute = ({ signInStatus }: { signInStatus: string }) => {
    const navigate = useNavigate();

    useEffect(() => {
      if (signInStatus !== "SIGNED_IN") {
        navigate("/");
      }
    }, [signInStatus, navigate]);

    return (
      <div className="flex flex-col min-h-screen border">
        <Outlet />
      </div>
    );
  };

  return (
    <Router>
      <HistoryProvider>
        <div className="min-h-full">
          <Routes>
            <Route
              path="/"
              element={
                <PublicRoute signInStatus={signInStatus}>
                  <Suspense fallback={<div className="text-black text-xs">Loading Login...</div>}><Login /></Suspense>
                </PublicRoute>
              }
            />
            <Route
              element={
                <AuthProvider
                  signOut={signOut}
                  setShowStepUpAuth={setShowStepUpAuth}
                  toggleShowAuthenticatorManager={toggleShowAuthenticatorManager}
                  tokensParsed={tokensParsed}
                  signInStatus={signInStatus}
                  idToken={tokens?.idToken}
                >
                  <ProtectedRoute signInStatus={signInStatus} />
                </AuthProvider>
              }
            >
            <Route path="/upload" element={<Suspense fallback={<div className="text-black text-xs">Loading Upload...</div>}><Upload /></Suspense>} />
            <Route path="/farms" element={<Suspense fallback={<div className="text-black text-xs">Loading Farms...</div>}><Farms /></Suspense>} />
            <Route path="/farms/add" element={<Suspense fallback={<div className="text-black text-xs">Loading Add Farm...</div>}><AddFarm /></Suspense>} />
            <Route path="/farms/view/:farmId" element={<Suspense fallback={<div className="text-black text-xs">Loading View Farm...</div>}><ViewFarm /></Suspense>} />
            <Route path="/samples" element={<Suspense fallback={<div className="text-black text-xs">Loading Samples...</div>}><Samples /></Suspense>} />
            <Route path="/samples/view" element={<Navigate to="/samples" replace />} />
            <Route path="/samples/view/:imageId" element={<Suspense fallback={<div className="text-black text-xs">Loading View Sample...</div>}><ViewSample /></Suspense>} />
            <Route path="/heatmap/demo" element={<Suspense fallback={<div className="text-black text-xs">Loading Infestation Heat Map...</div>}><UkHeatmapDemo /></Suspense>} />
            <Route
              path="/dashboard"
              element={<Suspense fallback={<div className="text-black text-xs">Loading Dashboard...</div>}>
                <Dashboard />
              </Suspense>} >

            </Route>
            <Route path="/contact" element={<Suspense fallback={<div className="text-black text-xs">Loading Contact...</div>}><Contact /></Suspense>} />
            <Route path="/stepupauth" element={<Suspense fallback={<div className="text-black text-xs">Loading Step Up Auth...</div>}><StepUpAuth /></Suspense>} />
            <Route path="/organization/dashboard" element={<Suspense fallback={<div className="text-black text-xs">Loading Organization Dashboard...</div>}><OrganizationDashboard /></Suspense>} />
            <Route path="/organization/membership" element={<Suspense fallback={<div className="text-black text-xs">Loading Membership...</div>}><Membership /></Suspense>} />
            <Route path="/organization/users" element={<Suspense fallback={<div className="text-black text-xs">Loading Organization Users...</div>}><OrganizationUsers /></Suspense>} />
            <Route path="/platform/organizations" element={<Suspense fallback={<div className="text-black text-xs">Loading Organizations...</div>}><Organizations /></Suspense>} />
            <Route path="/platform/users" element={<Suspense fallback={<div className="text-black text-xs">Loading Users...</div>}><Users /></Suspense>} />
          </Route>
        </Routes>
      </div>
      </HistoryProvider>
    </Router>
  );
};

export default App;
